import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { fetchNotes, addNote, deleteNote } from "../../services/api";
import { timeAgo } from "./constants";

export default function NotesSection({ contactId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchNotes(contactId)
      .then((data) => setNotes(data.notes || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [contactId]);

  const handleAdd = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      const note = await addNote(contactId, text.trim());
      setNotes((prev) => [note, ...prev]);
      setText("");
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteNote(contactId, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {
      // silently fail
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
        }}
      >
        Notes {!loading && notes.length > 0 && `(${notes.length})`}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="Add a note..."
          style={{
            flex: 1,
            padding: "7px 10px",
            fontSize: 12,
            fontFamily: fonts.body,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.bg,
            color: colors.text,
            outline: "none",
          }}
        />
        <button
          onClick={handleAdd}
          disabled={!text.trim() || saving}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 6,
            border: "none",
            background: text.trim() ? colors.accent : colors.bgActive,
            color: text.trim() ? "#ffffff" : colors.textSecondary,
            cursor: text.trim() ? "pointer" : "default",
            flexShrink: 0,
            transition: "all 0.15s",
          }}
        >
          <Plus size={14} />
        </button>
      </div>

      {loading ? (
        <div
          style={{
            padding: 12,
            textAlign: "center",
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Loader2 size={14} color={colors.textSecondary} className="spin" />
        </div>
      ) : notes.length === 0 ? (
        <div
          style={{
            padding: 12,
            fontSize: 12,
            color: colors.textSecondary,
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
          }}
        >
          No notes yet
        </div>
      ) : (
        <div
          style={{
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            overflow: "hidden",
          }}
        >
          {notes.map((note, i) => (
            <div
              key={note.id}
              style={{
                padding: "10px 12px",
                borderBottom:
                  i < notes.length - 1 ? `1px solid ${colors.border}` : "none",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: colors.text,
                  lineHeight: 1.5,
                  marginBottom: 4,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {note.text}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    fontFamily: fonts.mono,
                  }}
                >
                  {note.author} · {timeAgo(note.createdAt)}
                </div>
                <button
                  onClick={() => handleDelete(note.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: 4,
                    opacity: 0.4,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.4")}
                >
                  <Trash2 size={11} color={colors.danger} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
